/**
 * Pathology synoptic report panel.
 *
 * Template-driven: the shape of the form comes from
 * `synopticTemplates.ts`, not hardcoded inside this component.
 * The parent passes the current body (jsonb-persisted flat object)
 * and gets every edit back via onBodyChange so Save-draft / Sign-
 * finalize see the up-to-date values. The template id itself
 * lives inside the body under `_template` — switching templates
 * is a deliberate user action in the panel header, and re-sign
 * retains whichever template was most recently active.
 *
 * AI integration: fields marked `aiSource: "ki67"` auto-fill from
 * the `aiKi67Pct` prop (the Ki-67 overlay panel's scored index).
 * The value still honours the "only overwrite if user hasn't typed
 * anything, or if we put it there ourselves" rule so manual entries
 * aren't clobbered by a late AI result.
 */
import { createEffect, createSignal, For, Show } from "solid-js";
import type { PathAnno } from "./annotations";
import { Icd10Combobox } from "./Icd10Combobox";
import {
  SYNOPTIC_TEMPLATES, templateById, guessTemplate,
  type SynopticTemplate, type TemplateField,
} from "./synopticTemplates";

export interface SynopticPanelProps {
  caseId?:      string;
  patientName?: string;
  /** Number of annotations currently on the slide — shown in the
   *  header for quick sanity-check. */
  annotationCount: number;
  /** Most recent AI-scored Ki67 percentage, or null when no run yet. */
  aiKi67Pct?: number | null;
  /** Signed-off fires when the user clicks Finalise. The parent
   *  routes to the SR writer + marks the report signed in PACS.
   *  When omitted, the legacy in-panel Finalise button is hidden
   *  and the shell's footer owns finalisation. */
  onFinalise?: (report: SynopticReport) => void;
  /** Controlled-body: seed initial values + receive edits.
   *  The callback fires with the FULL updated body (not a patch)
   *  so the parent can simply replace — safer for cross-template
   *  switches where stale fields would otherwise leak through a
   *  spread-merge in the parent. */
  body?:          Record<string, any>;
  onBodyChange?:  (fullBody: Record<string, any>) => void;
  /** Optional hint strings the panel uses to auto-pick the best
   *  template on first mount (specimen description, study title,
   *  etc). Ignored if `body._template` is already set. */
  detectFrom?:    Array<string | undefined | null>;
}

export interface SynopticReport {
  templateId:  string;
  fields:      Record<string, any>;
  finalisedAt: number;
}

export function SynopticPanel(p: SynopticPanelProps) {
  // Active template. Seeded from body._template (most common path —
  // the report was saved earlier under a template) OR from the
  // heuristic guesser over `detectFrom` hints (fresh report) OR
  // the default breast-core template (everything else).
  const initialTemplate = () => {
    const saved = p.body?._template;
    if (saved) return templateById(saved);
    return guessTemplate(...(p.detectFrom ?? []));
  };
  const [tplId, setTplId] = createSignal<string>(initialTemplate().id);
  const tpl = (): SynopticTemplate => templateById(tplId());

  // Field values live in a plain reactive object. Writing a value
  // mutates the local object AND pushes the patch up to the parent
  // so Save-draft sees the latest body. Hydrating from `body`
  // guards against a write-back loop.
  let hydrating = false;
  const [values, setValues] = createSignal<Record<string, any>>({});

  createEffect(() => {
    const b = p.body;
    if (!b) return;
    hydrating = true;
    setValues({ ...b });
    if (b._template && b._template !== tplId()) setTplId(b._template);
    hydrating = false;
  });

  // Seed defaults the first time the template is picked (so an
  // empty body gets e.g. "Left breast, core biopsy" instead of
  // leaving the specimen blank). Pushes the FULL body back up
  // rather than just the patch so the parent can replace — keeps
  // the template-id authoritative through a template switch.
  createEffect(() => {
    const t = tpl();
    setValues((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const sec of t.sections) {
        for (const f of sec.fields) {
          if (f.default !== undefined && next[f.id] === undefined) {
            next[f.id] = f.default; changed = true;
          }
        }
      }
      if (!next._template) { next._template = t.id; changed = true; }
      if (changed && !hydrating) p.onBodyChange?.(next);
      return next;
    });
  });

  const set = (id: string, v: any) => {
    setValues((prev) => {
      const next = { ...prev, [id]: v };
      if (!hydrating) p.onBodyChange?.(next);
      return next;
    });
  };

  // Ki-67 AI auto-fill — only when the user hasn't typed a value
  // of their own, OR when our previous write was the source.
  createEffect(() => {
    const pct = p.aiKi67Pct;
    if (pct == null) return;
    const v = values();
    const cur = String(v.ki67Pct ?? "").trim();
    if (cur === "" || v.ki67FromAi) {
      set("ki67Pct", pct.toFixed(1));
      set("ki67FromAi", true);
    }
  });

  const [finalisedAt, setFinalisedAt] = createSignal<number | null>(null);
  function finalise() {
    const rpt: SynopticReport = {
      templateId: tplId(),
      fields:     { ...values() },
      finalisedAt: Date.now(),
    };
    setFinalisedAt(rpt.finalisedAt);
    p.onFinalise?.(rpt);
  }

  function switchTemplate(newId: string) {
    // Picking a new template keeps shared fields (specimen,
    // diagnosis, comment) but drops template-specific ones that
    // aren't present in the new schema. `_template` is NOT copied
    // from prev even though the old version of this code had it
    // in `keep` — the copy loop would otherwise immediately
    // restore the previous template id and the switch would
    // appear to succeed in the UI while the save/reload round-trip
    // kept resolving back to the original template.
    const newTpl = templateById(newId);
    const keep = new Set<string>();
    for (const sec of newTpl.sections) for (const f of sec.fields) keep.add(f.id);
    setValues((prev) => {
      const next: Record<string, any> = {};
      for (const k of Object.keys(prev)) {
        if (keep.has(k) && k !== "_template") next[k] = prev[k];
      }
      next._template = newId;  // ← authoritative, set AFTER the copy
      // Seed defaults for any newly-relevant field that the prev
      // template didn't carry.
      for (const sec of newTpl.sections) for (const f of sec.fields) {
        if (next[f.id] === undefined && f.default !== undefined) next[f.id] = f.default;
      }
      p.onBodyChange?.(next);
      return next;
    });
    setTplId(newId);
  }

  return (
    // No `h-full` and no inner `overflow-auto` — the parent in
    // PathologyShell wraps Report tab content (SynopticPanel +
    // SnapshotsSection) in a single `overflow-auto` so the user
    // gets ONE scroll container. Two nested scrollers ate the
    // mouse-wheel event because the inner one consumed it before
    // the outer could see it.
    <div class="flex flex-col text-[11px]">
      {/* Header — case banner + template picker + annotation count */}
      <div class="flex flex-wrap items-center gap-2 border-b border-ink-300 px-3 py-2">
        <span class="chip-brand !text-[9px]">Report</span>
        <Show when={p.caseId}>
          <span class="truncate font-mono text-ink-900">{p.caseId}</span>
        </Show>
        <span class="ml-auto text-[10px] text-ink-700">
          annotations <span class="font-mono text-ink-900">{p.annotationCount}</span>
        </span>
      </div>
      <div class="flex flex-wrap items-center gap-2 border-b border-ink-300 bg-ink-50 px-3 py-1.5">
        <span class="text-[10px] uppercase tracking-wider text-ink-700">template</span>
        <select class="flex-1 rounded border border-ink-300 bg-ink-100 px-1.5 py-0.5 text-[11px] text-ink-900 focus:border-brand focus:outline-none"
                value={tplId()}
                onChange={(e) => switchTemplate((e.currentTarget as HTMLSelectElement).value)}>
          <For each={SYNOPTIC_TEMPLATES}>{(t) => (
            <option value={t.id}>{t.name}</option>
          )}</For>
        </select>
      </div>
      <Show when={tpl().description}>
        <p class="border-b border-ink-300 bg-ink-50 px-3 py-1 text-[9px] text-ink-700">
          {tpl().description}
        </p>
      </Show>

      {/* Body — sections + fields. NO inner overflow-auto: parent
          dock owns scrolling so SynopticPanel + SnapshotsSection
          live in one scrollable column. */}
      <div class="p-3">
        <For each={tpl().sections}>{(sec) => (
          <section class="mb-4">
            <div class="mb-1 border-b border-ink-300 pb-0.5 text-[9px] uppercase tracking-wider text-ink-700">
              {sec.title}
            </div>
            <div class="gap-2"
                 classList={{
                   "grid grid-cols-1": !sec.layout || sec.layout === "cols-1",
                   "grid grid-cols-2": sec.layout === "cols-2",
                   "grid grid-cols-3": sec.layout === "cols-3",
                   "grid grid-cols-4": sec.layout === "cols-4",
                 }}>
              <For each={sec.fields}>{(f) => <FieldEl f={f} value={values()[f.id]} set={set} fromAi={f.id === "ki67Pct" && !!values().ki67FromAi} />}</For>
            </div>
          </section>
        )}</For>
      </div>

      {/* Legacy Finalise button — only shown when the parent wires
          its own onFinalise handler. The new PathologyShell has a
          canonical draft/finalize/amend footer below this panel,
          so this button would be a confusing duplicate there. */}
      <Show when={p.onFinalise}>
        <div class="border-t border-ink-300 p-3">
          <button type="button"
                  class="btn-brand w-full text-xs"
                  disabled={finalisedAt() != null}
                  onClick={finalise}>
            {finalisedAt() != null
              ? `Finalised ${new Date(finalisedAt()!).toLocaleTimeString()}`
              : "Finalise & sign"}
          </button>
        </div>
      </Show>
    </div>
  );
}

/** One form row — label above + input below, layout follows the
 *  field's type. Compact; 11px text; the input inherits the ink
 *  chrome so it matches the rest of the shell. */
function FieldEl(fp: { f: TemplateField; value: any; set: (id: string, v: any) => void; fromAi: boolean }) {
  const base = "w-full rounded border border-ink-300 bg-ink-200 px-2 py-1 text-[11px] text-ink-900 focus:border-brand focus:outline-none";
  return (
    <label class="block">
      <div class="mb-0.5 flex items-center gap-1 text-[9px] uppercase tracking-wider text-ink-700">
        <span>{fp.f.label}</span>
        <Show when={fp.f.hint}><span class="text-ink-700 normal-case">· {fp.f.hint}</span></Show>
        <Show when={fp.fromAi}>
          <span class="rounded bg-brand/20 px-1 py-0.5 text-[9px] tracking-wider text-brand">AI</span>
        </Show>
      </div>
      <Show when={fp.f.type === "text"}>
        <input type="text" class={base}
               placeholder={fp.f.placeholder}
               value={fp.value ?? ""}
               onInput={(e) => fp.set(fp.f.id, (e.currentTarget as HTMLInputElement).value)} />
      </Show>
      <Show when={fp.f.type === "number"}>
        <input type="number" class={`${base} text-right font-mono`}
               step={fp.f.step} min={fp.f.min} max={fp.f.max}
               placeholder={fp.f.placeholder}
               value={fp.value ?? ""}
               onInput={(e) => {
                 const v = (e.currentTarget as HTMLInputElement).value;
                 fp.set(fp.f.id, v);
                 if (fp.f.id === "ki67Pct") fp.set("ki67FromAi", false);
               }} />
      </Show>
      <Show when={fp.f.type === "select"}>
        <select class={base}
                classList={{ "border-brand": fp.fromAi }}
                value={fp.value ?? (fp.f.default ?? "")}
                onChange={(e) => fp.set(fp.f.id, (e.currentTarget as HTMLSelectElement).value)}>
          <For each={fp.f.options ?? []}>{(opt) => (
            <option value={opt}>{opt}</option>
          )}</For>
        </select>
      </Show>
      <Show when={fp.f.type === "textarea"}>
        <textarea rows={fp.f.rows ?? 3} class={base}
                  placeholder={fp.f.placeholder}
                  value={fp.value ?? ""}
                  onInput={(e) => fp.set(fp.f.id, (e.currentTarget as HTMLTextAreaElement).value)} />
      </Show>
      <Show when={fp.f.type === "date"}>
        <input type="date" class={base}
               value={fp.value ?? ""}
               onInput={(e) => fp.set(fp.f.id, (e.currentTarget as HTMLInputElement).value)} />
      </Show>
      <Show when={fp.f.type === "icd10"}>
        <Icd10Combobox
          value={Array.isArray(fp.value) ? fp.value : []}
          onChange={(next) => fp.set(fp.f.id, next)} />
      </Show>
    </label>
  );
}
